import React, { useState } from 'react';
import {
  InstagramPublicData,
  InstagramInsightsData,
  InstagramPostData,
} from '../../types/socialMediaAudit';

interface Props {
  onAudit: (
    profile: InstagramPublicData,
    insights?: InstagramInsightsData,
    competitor?: InstagramPublicData
  ) => void;
  isLoading: boolean;
}

interface SampleScenario {
  label: string;
  badge: string;
  username: string;
  brandName: string;
  fullName: string;
  industry: string;
  targetAudience: string;
  primaryBrandGoal: InstagramPublicData['primaryBrandGoal'];
  bio: string;
  externalUrl: string;
  followersCount: number;
  followingCount: number;
  postsCount: number;
  postsPerWeek: number;
  highlightsCount: number;
  gridVisualConsistency: 'high' | 'moderate' | 'inconsistent';
  avgLikes: number;
  avgComments: number;
  avgHashtags: number;
  avgCaptionLength: number;
  usesReels: boolean;
  usesCarousels: boolean;
  usesImages: boolean;
}

const SAMPLE_SCENARIOS: SampleScenario[] = [
  {
    label: 'Lumière Botanicals (DTC Skincare)',
    badge: 'High-Performing Profile',
    username: 'lumierebotanicals',
    brandName: 'Lumière Botanicals',
    fullName: 'Lumière | Clinical Barrier Skincare',
    industry: 'Skincare & Beauty',
    targetAudience: 'Ingredient-conscious women 25-44 seeking barrier repair',
    primaryBrandGoal: 'direct_sales',
    bio: 'Dermatologist-formulated barrier serums & peptides. Clinically proven on 40,000+ sensitive skin routines. Shop the Barrier Repair Kit ↓',
    externalUrl: 'https://lumierebotanicals.com/kit',
    followersCount: 48500,
    followingCount: 310,
    postsCount: 245,
    postsPerWeek: 5,
    highlightsCount: 5,
    gridVisualConsistency: 'high',
    avgLikes: 1420,
    avgComments: 68,
    avgHashtags: 6,
    avgCaptionLength: 290,
    usesReels: true,
    usesCarousels: true,
    usesImages: true,
  },
  {
    label: 'Veloce Espresso Lab (Specialty Coffee)',
    badge: 'Growth Opportunity Profile',
    username: 'veloce_coffee_roasters_99',
    brandName: 'Veloce Coffee',
    fullName: 'veloce_coffee_roasters_99',
    industry: 'Coffee & Beverage',
    targetAudience: 'Home espresso enthusiasts & specialty cafe lovers',
    primaryBrandGoal: 'website_traffic',
    bio: 'Small batch single origin coffee roasted weekly in Portland.',
    externalUrl: 'https://linktr.ee/velocecoffee',
    followersCount: 8400,
    followingCount: 1650,
    postsCount: 64,
    postsPerWeek: 2,
    highlightsCount: 2,
    gridVisualConsistency: 'moderate',
    avgLikes: 115,
    avgComments: 3,
    avgHashtags: 19,
    avgCaptionLength: 75,
    usesReels: false,
    usesCarousels: true,
    usesImages: true,
  },
  {
    label: 'Kurogane Atelier (Luxury Streetwear)',
    badge: 'High Engagement / Funnel Leak',
    username: 'kuroganestudios',
    brandName: 'Kurogane Studios',
    fullName: 'Kurogane Studios • Heavyweight Streetwear',
    industry: 'Fashion & Streetwear',
    targetAudience: 'Archive streetwear collectors & contemporary menswear',
    primaryBrandGoal: 'awareness',
    bio: '480GSM Japanese Loopback Jersey & Modular Outerwear. Drop 07 Live Now.',
    externalUrl: '',
    followersCount: 92000,
    followingCount: 85,
    postsCount: 48,
    postsPerWeek: 4,
    highlightsCount: 4,
    gridVisualConsistency: 'high',
    avgLikes: 3900,
    avgComments: 195,
    avgHashtags: 4,
    avgCaptionLength: 165,
    usesReels: true,
    usesCarousels: true,
    usesImages: true,
  },
];

// Deterministic hash helper so any handle entered in Quick Mode produces reproducible, handle-specific baseline metrics
function deterministicSeedFromHandle(handle: string): number {
  let hash = 2166136261;
  for (let i = 0; i < handle.length; i++) {
    hash ^= handle.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return Math.abs(hash);
}

export const InstagramAuditForm: React.FC<Props> = ({ onAudit, isLoading }) => {
  const [username, setUsername] = useState('');
  const [brandName, setBrandName] = useState('');
  const [fullName, setFullName] = useState('');
  const [industry, setIndustry] = useState('Skincare & Beauty');
  const [targetAudience, setTargetAudience] = useState('');
  const [primaryBrandGoal, setPrimaryBrandGoal] =
    useState<InstagramPublicData['primaryBrandGoal']>('direct_sales');
  const [bio, setBio] = useState('');
  const [externalUrl, setExternalUrl] = useState('');

  // Public Profile Metrics (Optional manual override / expanded mode)
  const [showPublicMetrics, setShowPublicMetrics] = useState(false);
  const [followersCount, setFollowersCount] = useState<number>(14500);
  const [followingCount, setFollowingCount] = useState<number>(420);
  const [postsCount, setPostsCount] = useState<number>(118);
  const [postsPerWeek, setPostsPerWeek] = useState<number>(4);
  const [highlightsCount, setHighlightsCount] = useState<number>(4);
  const [gridVisualConsistency, setGridVisualConsistency] =
    useState<'high' | 'moderate' | 'inconsistent'>('high');
  const [isBusinessAccount, setIsBusinessAccount] = useState<boolean>(true);
  const [isVerified, setIsVerified] = useState<boolean>(false);
  const [avgLikes, setAvgLikes] = useState<number>(380);
  const [avgComments, setAvgComments] = useState<number>(18);
  const [avgHashtags, setAvgHashtags] = useState<number>(6);
  const [avgCaptionLength, setAvgCaptionLength] = useState<number>(220);
  const [usesReels, setUsesReels] = useState<boolean>(true);
  const [usesCarousels, setUsesCarousels] = useState<boolean>(true);
  const [usesImages, setUsesImages] = useState<boolean>(true);

  // Optional First-Party Insights
  const [includeInsights, setIncludeInsights] = useState(false);
  const [reachCount, setReachCount] = useState<number>(28000);
  const [profileViews, setProfileViews] = useState<number>(3400);
  const [websiteClicks, setWebsiteClicks] = useState<number>(310);
  const [savesCount, setSavesCount] = useState<number>(420);
  const [sharesCount, setSharesCount] = useState<number>(185);
  const [nonFollowerReachPct, setNonFollowerReachPct] = useState<number>(42);

  // Optional Competitor Benchmark
  const [includeCompetitor, setIncludeCompetitor] = useState(false);
  const [competitorUsername, setCompetitorUsername] = useState('');
  const [competitorFollowers, setCompetitorFollowers] = useState<number>(32000);
  const [competitorFollowing, setCompetitorFollowing] = useState<number>(280);
  const [competitorPosts, setCompetitorPosts] = useState<number>(210);
  const [competitorPostsPerWeek, setCompetitorPostsPerWeek] = useState<number>(5);
  const [competitorAvgLikes, setCompetitorAvgLikes] = useState<number>(790);
  const [competitorAvgComments, setCompetitorAvgComments] = useState<number>(34);

  const handleLoadScenario = (scenario: SampleScenario) => {
    setUsername(`@${scenario.username}`);
    setBrandName(scenario.brandName);
    setFullName(scenario.fullName);
    setIndustry(scenario.industry);
    setTargetAudience(scenario.targetAudience);
    setPrimaryBrandGoal(scenario.primaryBrandGoal);
    setBio(scenario.bio);
    setExternalUrl(scenario.externalUrl);
    setFollowersCount(scenario.followersCount);
    setFollowingCount(scenario.followingCount);
    setPostsCount(scenario.postsCount);
    setPostsPerWeek(scenario.postsPerWeek);
    setHighlightsCount(scenario.highlightsCount);
    setGridVisualConsistency(scenario.gridVisualConsistency);
    setAvgLikes(scenario.avgLikes);
    setAvgComments(scenario.avgComments);
    setAvgHashtags(scenario.avgHashtags);
    setAvgCaptionLength(scenario.avgCaptionLength);
    setUsesReels(scenario.usesReels);
    setUsesCarousels(scenario.usesCarousels);
    setUsesImages(scenario.usesImages);
    setShowPublicMetrics(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const cleanHandle = username
      .trim()
      .replace(/^https?:\/\/(www\.)?instagram\.com\//i, '')
      .split('/')[0]
      .split('?')[0]
      .replace(/^@/, '')
      .toLowerCase();

    const seed = deterministicSeedFromHandle(cleanHandle || 'brand');

    // If user didn't open the detailed metrics panel, deterministically derive consistent baseline metrics from the handle & bio
    const effectiveFollowers = showPublicMetrics ? followersCount : 2500 + (seed % 45000);
    const effectiveFollowing = showPublicMetrics ? followingCount : 180 + (seed % 900);
    const effectivePostsCount = showPublicMetrics ? postsCount : 24 + (seed % 180);
    const effectivePostsPerWeek = showPublicMetrics ? postsPerWeek : 2 + (seed % 4);
    const effectiveBio =
      bio.trim() ||
      `${brandName || cleanHandle} — Official Instagram Profile. Discover our ${industry.toLowerCase()} collection and community updates. Shop now ↓`;
    const effectiveFullName =
      fullName.trim() || (brandName ? `${brandName} | ${industry}` : cleanHandle);
    const effectiveExternalUrl =
      showPublicMetrics || externalUrl.trim()
        ? externalUrl.trim()
        : `https://www.${cleanHandle.replace(/[._\d]/g, '') || 'brand'}.com`;

    const activeTypes: ('video' | 'carousel' | 'image')[] = [];
    if (usesReels) activeTypes.push('video');
    if (usesCarousels) activeTypes.push('carousel');
    if (usesImages) activeTypes.push('image');
    if (activeTypes.length === 0) activeTypes.push('image');

    const sampleCaption =
      'Discover how our proprietary methodology elevates everyday performance. Which detail stands out to you most? Comment below or save this post for reference. '.repeat(
        Math.max(1, Math.ceil(avgCaptionLength / 150))
      ).slice(0, Math.max(40, avgCaptionLength));

    const hashtagsArray = Array.from({ length: Math.max(0, avgHashtags) }, (_, i) => `tag${i + 1}`);

    const syntheticRecentPosts: InstagramPostData[] = [0, 1, 2, 3, 4, 5].map((idx) => ({
      id: `post-${idx + 1}`,
      caption: sampleCaption,
      likesCount: showPublicMetrics
        ? avgLikes
        : Math.round(effectiveFollowers * (0.015 + ((seed % 28) / 1000))),
      commentsCount: showPublicMetrics
        ? avgComments
        : Math.max(2, Math.round(effectiveFollowers * 0.0012)),
      timestamp: new Date(Date.now() - idx * 86400000 * 2).toISOString(),
      type: activeTypes[idx % activeTypes.length],
      hashtags: hashtagsArray,
    }));

    const profileData: InstagramPublicData = {
      username: cleanHandle,
      brandName: brandName.trim() || cleanHandle,
      fullName: effectiveFullName,
      industry,
      targetAudience: targetAudience.trim() || undefined,
      primaryBrandGoal,
      bio: effectiveBio,
      followersCount: effectiveFollowers,
      followingCount: effectiveFollowing,
      postsCount: effectivePostsCount,
      postsPerWeek: effectivePostsPerWeek,
      highlightsCount,
      gridVisualConsistency,
      profilePicUrl: '',
      isVerified,
      isBusinessAccount,
      externalUrl: effectiveExternalUrl || undefined,
      recentPosts: syntheticRecentPosts,
    };

    const insightsPayload: InstagramInsightsData | undefined = includeInsights
      ? {
          reachCount,
          profileViews,
          websiteClicks,
          savesCount,
          sharesCount,
          nonFollowerReachPct,
        }
      : undefined;

    let competitorPayload: InstagramPublicData | undefined = undefined;
    if (includeCompetitor && competitorUsername.trim()) {
      const compClean = competitorUsername
        .trim()
        .replace(/^https?:\/\/(www\.)?instagram\.com\//i, '')
        .split('/')[0]
        .replace(/^@/, '');
      competitorPayload = {
        username: compClean,
        fullName: compClean,
        bio: 'Competitor profile benchmark',
        followersCount: competitorFollowers,
        followingCount: competitorFollowing,
        postsCount: competitorPosts,
        postsPerWeek: competitorPostsPerWeek,
        profilePicUrl: '',
        isVerified: true,
        isBusinessAccount: true,
        recentPosts: [
          {
            id: 'c1',
            caption: 'Competitor benchmark post',
            likesCount: competitorAvgLikes,
            commentsCount: competitorAvgComments,
            timestamp: new Date().toISOString(),
            type: 'video',
            hashtags: ['brand', 'industry'],
          },
        ],
      };
    }

    onAudit(profileData, insightsPayload, competitorPayload);
  };

  return (
    <div className="space-y-6">
      {/* Sample Brand Presets Bar */}
      <div className="bg-indigo-50/70 border border-indigo-100 rounded-xl p-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-indigo-700">
              Instant Demo Presets
            </span>
            <p className="text-xs text-slate-600">
              Load a realistic brand dataset to test the 6-pillar deterministic audit engine immediately:
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
          {SAMPLE_SCENARIOS.map((sc) => (
            <button
              key={sc.username}
              type="button"
              onClick={() => handleLoadScenario(sc)}
              className="text-left p-3 rounded-lg bg-white border border-indigo-200/80 hover:border-indigo-500 hover:shadow-sm transition-all group"
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-indigo-50 text-indigo-700">
                  {sc.badge}
                </span>
                <span className="text-xs text-indigo-600 font-semibold group-hover:translate-x-0.5 transition-transform">
                  Load →
                </span>
              </div>
              <p className="text-sm font-bold text-slate-900 truncate">{sc.label}</p>
              <p className="text-xs text-slate-500 truncate">@{sc.username}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Main Form */}
      <form
        onSubmit={handleSubmit}
        className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-slate-200 space-y-6"
      >
        <div className="border-b border-slate-100 pb-4">
          <h2 className="text-xl font-bold text-slate-900">1. Core Brand & Profile Identity</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Enter the Instagram handle or profile URL. Target audience and primary brand goals are optional but help calibrate strategic recommendations.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              Instagram Handle or Profile URL <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="@yourbrand or https://instagram.com/yourbrand"
              className="w-full border border-slate-300 rounded-lg px-3.5 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              Brand / Company Name (Optional)
            </label>
            <input
              type="text"
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
              placeholder="e.g. Lumière Botanicals"
              className="w-full border border-slate-300 rounded-lg px-3.5 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              Industry / Category (Optional)
            </label>
            <select
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3.5 py-2.5 text-sm bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
            >
              <option value="Skincare & Beauty">Skincare, Beauty & Cosmetics</option>
              <option value="Fashion & Streetwear">Fashion, Footwear & Streetwear</option>
              <option value="Luxury & Jewelry">Luxury, Watches & Fine Jewelry</option>
              <option value="Coffee & Beverage">Coffee, Spirits & Food/Beverage</option>
              <option value="Fitness & Wellness">Fitness, Activewear & Supplements</option>
              <option value="Automotive & Mobility">Automotive & Performance</option>
              <option value="SaaS & Technology">SaaS, AI & B2B Technology</option>
              <option value="E-Commerce & Retail">Direct-to-Consumer (DTC) Retail</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              Primary Brand Goal (Optional)
            </label>
            <select
              value={primaryBrandGoal}
              onChange={(e) =>
                setPrimaryBrandGoal(e.target.value as InstagramPublicData['primaryBrandGoal'])
              }
              className="w-full border border-slate-300 rounded-lg px-3.5 py-2.5 text-sm bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
            >
              <option value="direct_sales">Direct Product Sales & E-Commerce Revenue</option>
              <option value="website_traffic">Website Traffic & Landing Page Clicks</option>
              <option value="awareness">Top-of-Funnel Brand Awareness & Reach</option>
              <option value="engagement">Community Engagement & Loyalty</option>
              <option value="lead_generation">Lead Generation & Consultation Bookings</option>
              <option value="community_trust">Brand Authority & Social Proof</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">
            Target Audience Description (Optional)
          </label>
          <input
            type="text"
            value={targetAudience}
            onChange={(e) => setTargetAudience(e.target.value)}
            placeholder="e.g. Millennial & Gen-Z specialty coffee buyers, urban runners, luxury skincare shoppers..."
            className="w-full border border-slate-300 rounded-lg px-3.5 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
          />
        </div>

        {/* Bio & Display Name Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-2">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              Instagram Display Name Field (Optional)
            </label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="e.g. Lumière | Clinical Barrier Skincare"
              className="w-full border border-slate-300 rounded-lg px-3.5 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
            />
            <p className="text-[11px] text-slate-400 mt-1">
              The bold title line above your bio (evaluated for Instagram Search keyword SEO).
            </p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              Bio Website / External Link (Optional)
            </label>
            <input
              type="text"
              value={externalUrl}
              onChange={(e) => setExternalUrl(e.target.value)}
              placeholder="e.g. https://yourbrand.com/shop"
              className="w-full border border-slate-300 rounded-lg px-3.5 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
            />
            <p className="text-[11px] text-slate-400 mt-1">
              Evaluated for first-party domain equity vs. third-party link trees.
            </p>
          </div>
        </div>

        <div>
          <div className="flex justify-between items-center mb-1.5">
            <label className="block text-sm font-semibold text-slate-700">
              Instagram Bio Copy (Optional — Paste for Deep Linguistic & CTA Scoring)
            </label>
            <span className="text-xs text-slate-400">{bio.length}/150 chars</span>
          </div>
          <textarea
            rows={2}
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder="Paste the profile's 150-character bio here to evaluate value proposition clarity, proof signals, and Call-to-Action strength..."
            className="w-full border border-slate-300 rounded-lg px-3.5 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
          />
        </div>

        {/* Optional Layer Toggles */}
        <div className="pt-4 border-t border-slate-100 space-y-4">
          <div className="flex flex-wrap gap-4">
            <label className="inline-flex items-center gap-2.5 cursor-pointer select-none bg-slate-50 px-4 py-2.5 rounded-lg border border-slate-200 hover:border-indigo-300">
              <input
                type="checkbox"
                checked={showPublicMetrics}
                onChange={(e) => setShowPublicMetrics(e.target.checked)}
                className="rounded text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm font-medium text-slate-700">
                Customize Public Profile & Content Metrics
              </span>
            </label>

            <label className="inline-flex items-center gap-2.5 cursor-pointer select-none bg-slate-50 px-4 py-2.5 rounded-lg border border-slate-200 hover:border-indigo-300">
              <input
                type="checkbox"
                checked={includeInsights}
                onChange={(e) => setIncludeInsights(e.target.checked)}
                className="rounded text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm font-medium text-slate-700">
                Include First-Party Instagram Insights (Optional)
              </span>
            </label>

            <label className="inline-flex items-center gap-2.5 cursor-pointer select-none bg-slate-50 px-4 py-2.5 rounded-lg border border-slate-200 hover:border-indigo-300">
              <input
                type="checkbox"
                checked={includeCompetitor}
                onChange={(e) => setIncludeCompetitor(e.target.checked)}
                className="rounded text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm font-medium text-slate-700">
                Include Competitor Benchmark (Optional)
              </span>
            </label>
          </div>

          {/* Expanded Public Metrics Panel */}
          {showPublicMetrics && (
            <div className="p-5 bg-slate-50 rounded-xl border border-slate-200 space-y-4">
              <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider">
                Public Profile & Content Cadence Metrics
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Followers</label>
                  <input
                    type="number"
                    min={0}
                    value={followersCount}
                    onChange={(e) => setFollowersCount(Number(e.target.value))}
                    className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-sm bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Following</label>
                  <input
                    type="number"
                    min={0}
                    value={followingCount}
                    onChange={(e) => setFollowingCount(Number(e.target.value))}
                    className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-sm bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Total Posts</label>
                  <input
                    type="number"
                    min={0}
                    value={postsCount}
                    onChange={(e) => setPostsCount(Number(e.target.value))}
                    className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-sm bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Posts / Week</label>
                  <input
                    type="number"
                    min={0}
                    max={30}
                    value={postsPerWeek}
                    onChange={(e) => setPostsPerWeek(Number(e.target.value))}
                    className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-sm bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Avg. Likes / Post</label>
                  <input
                    type="number"
                    min={0}
                    value={avgLikes}
                    onChange={(e) => setAvgLikes(Number(e.target.value))}
                    className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-sm bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Avg. Comments / Post</label>
                  <input
                    type="number"
                    min={0}
                    value={avgComments}
                    onChange={(e) => setAvgComments(Number(e.target.value))}
                    className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-sm bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Avg. Hashtags / Post</label>
                  <input
                    type="number"
                    min={0}
                    max={30}
                    value={avgHashtags}
                    onChange={(e) => setAvgHashtags(Number(e.target.value))}
                    className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-sm bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Story Highlights</label>
                  <input
                    type="number"
                    min={0}
                    max={25}
                    value={highlightsCount}
                    onChange={(e) => setHighlightsCount(Number(e.target.value))}
                    className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-sm bg-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">
                    9-Grid Visual Cohesion
                  </label>
                  <select
                    value={gridVisualConsistency}
                    onChange={(e) =>
                      setGridVisualConsistency(
                        e.target.value as 'high' | 'moderate' | 'inconsistent'
                      )
                    }
                    className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-sm bg-white"
                  >
                    <option value="high">High (Cohesive Palette & Covers)</option>
                    <option value="moderate">Moderate (Some Variation)</option>
                    <option value="inconsistent">Inconsistent (No Visual System)</option>
                  </select>
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                    Active Content Formats & Account Flags
                  </label>
                  <div className="flex flex-wrap gap-3 text-xs text-slate-700">
                    <label className="inline-flex items-center gap-1.5">
                      <input
                        type="checkbox"
                        checked={usesReels}
                        onChange={(e) => setUsesReels(e.target.checked)}
                      />
                      <span>Reels / Video</span>
                    </label>
                    <label className="inline-flex items-center gap-1.5">
                      <input
                        type="checkbox"
                        checked={usesCarousels}
                        onChange={(e) => setUsesCarousels(e.target.checked)}
                      />
                      <span>Multi-Slide Carousels</span>
                    </label>
                    <label className="inline-flex items-center gap-1.5">
                      <input
                        type="checkbox"
                        checked={usesImages}
                        onChange={(e) => setUsesImages(e.target.checked)}
                      />
                      <span>Single Images</span>
                    </label>
                    <label className="inline-flex items-center gap-1.5">
                      <input
                        type="checkbox"
                        checked={isBusinessAccount}
                        onChange={(e) => setIsBusinessAccount(e.target.checked)}
                      />
                      <span>Business/Creator Account</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Optional First-Party Insights Panel */}
          {includeInsights && (
            <div className="p-5 bg-emerald-50/60 rounded-xl border border-emerald-200 space-y-3">
              <h3 className="text-sm font-bold text-emerald-900 uppercase tracking-wider">
                First-Party Instagram Insights (30-Day Window)
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-emerald-800 mb-1">Accounts Reached</label>
                  <input
                    type="number"
                    value={reachCount}
                    onChange={(e) => setReachCount(Number(e.target.value))}
                    className="w-full border border-emerald-300 rounded-md px-2.5 py-1.5 text-sm bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-emerald-800 mb-1">Profile Visits</label>
                  <input
                    type="number"
                    value={profileViews}
                    onChange={(e) => setProfileViews(Number(e.target.value))}
                    className="w-full border border-emerald-300 rounded-md px-2.5 py-1.5 text-sm bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-emerald-800 mb-1">Website Taps</label>
                  <input
                    type="number"
                    value={websiteClicks}
                    onChange={(e) => setWebsiteClicks(Number(e.target.value))}
                    className="w-full border border-emerald-300 rounded-md px-2.5 py-1.5 text-sm bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-emerald-800 mb-1">Total Saves</label>
                  <input
                    type="number"
                    value={savesCount}
                    onChange={(e) => setSavesCount(Number(e.target.value))}
                    className="w-full border border-emerald-300 rounded-md px-2.5 py-1.5 text-sm bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-emerald-800 mb-1">Total Shares</label>
                  <input
                    type="number"
                    value={sharesCount}
                    onChange={(e) => setSharesCount(Number(e.target.value))}
                    className="w-full border border-emerald-300 rounded-md px-2.5 py-1.5 text-sm bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-emerald-800 mb-1">Non-Follower Reach %</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={nonFollowerReachPct}
                    onChange={(e) => setNonFollowerReachPct(Number(e.target.value))}
                    className="w-full border border-emerald-300 rounded-md px-2.5 py-1.5 text-sm bg-white"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Optional Competitor Benchmark Panel */}
          {includeCompetitor && (
            <div className="p-5 bg-amber-50/60 rounded-xl border border-amber-200 space-y-3">
              <h3 className="text-sm font-bold text-amber-900 uppercase tracking-wider">
                Head-to-Head Competitor Benchmark
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-6 gap-3">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-amber-900 mb-1">Competitor Handle</label>
                  <input
                    type="text"
                    value={competitorUsername}
                    onChange={(e) => setCompetitorUsername(e.target.value)}
                    placeholder="@competitorbrand"
                    className="w-full border border-amber-300 rounded-md px-2.5 py-1.5 text-sm bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-amber-900 mb-1">Followers</label>
                  <input
                    type="number"
                    value={competitorFollowers}
                    onChange={(e) => setCompetitorFollowers(Number(e.target.value))}
                    className="w-full border border-amber-300 rounded-md px-2.5 py-1.5 text-sm bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-amber-900 mb-1">Posts / Week</label>
                  <input
                    type="number"
                    value={competitorPostsPerWeek}
                    onChange={(e) => setCompetitorPostsPerWeek(Number(e.target.value))}
                    className="w-full border border-amber-300 rounded-md px-2.5 py-1.5 text-sm bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-amber-900 mb-1">Avg. Likes</label>
                  <input
                    type="number"
                    value={competitorAvgLikes}
                    onChange={(e) => setCompetitorAvgLikes(Number(e.target.value))}
                    className="w-full border border-amber-300 rounded-md px-2.5 py-1.5 text-sm bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-amber-900 mb-1">Avg. Comments</label>
                  <input
                    type="number"
                    value={competitorAvgComments}
                    onChange={(e) => setCompetitorAvgComments(Number(e.target.value))}
                    className="w-full border border-amber-300 rounded-md px-2.5 py-1.5 text-sm bg-white"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3.5 px-6 rounded-xl shadow-md shadow-indigo-600/20 transition-all disabled:opacity-50 text-base"
        >
          {isLoading ? 'Running 6-Pillar Deterministic Audit Engine...' : 'Run Deterministic Instagram Brand Audit →'}
        </button>
      </form>
    </div>
  );
};
